import { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getAllNotices } from '../../api/noticeApi';
import { getMyEvents } from '../../api/eventApi';
import { useNavigate } from 'react-router-dom';
import fileUrl from '../../utils/fileUrl';

const CATEGORY_COLORS = { General: '#94A3B8', Incentive: '#F59E0B', News: '#1A8CFF', Update: '#10B981' };

export default function DashboardSlideshow() {
    const [slides, setSlides] = useState([]);
    const [current, setCurrent] = useState(0);
    const navigate = useNavigate();
    // Up here with the other hooks, not beside the handlers that use it: there is an
    // early return below for the empty case, and a hook after it would not run on every
    // render.
    const touch = useRef({ x: 0, y: 0, moved: false });

    // Notice slides, plus a slide for every upcoming event this person is invited to
    // that carries a banner.
    //
    // The events come from GetMine, which resolves each event's audience on the SERVER
    // and redacts a private event to a title-only teaser for anyone outside it. So a
    // private event's banner reaches its invitees and nobody else - which is why the
    // banner is read from here rather than copied onto the notice, where it could only
    // ever have been all-or-nothing. (user 2026-10-03)
    const fetchSlides = useCallback(async () => {
        const [noticeRes, eventRes] = await Promise.allSettled([getAllNotices(), getMyEvents()]);

        const noticeSlides = noticeRes.status === 'fulfilled'
            ? (noticeRes.value.data || []).filter(n => n.type === 'Slideshow' || n.showInSlideshow)
            : [];

        const now = Date.now();
        const eventSlides = eventRes.status === 'fulfilled'
            ? (eventRes.value.data || [])
                .filter(e => !e.isTeaser && e.bannerUrl && e.status === 'Published')
                .filter(e => e.nextSession?.endsOn && new Date(e.nextSession.endsOn).getTime() >= now)
                .sort((a, b) => new Date(a.nextSession.startsOn) - new Date(b.nextSession.startsOn))
                .map(e => ({
                    noticeId: `event-${e.eventId}`,
                    eventId: e.eventId,
                    title: e.title,
                    content: new Date(e.nextSession.startsOn).toLocaleString('en-MY', {
                        weekday: 'long', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
                    }) + (!e.isOnline && e.venue ? ` · ${e.venue}` : ''),
                    category: e.type === 'Recruitment' ? 'News' : e.type === 'Training' ? 'Update' : 'Incentive',
                    slideshowImageUrl: e.bannerUrl,
                }))
            : [];

        // The next event leads, since it is the thing with a date on it.
        setSlides([...eventSlides, ...noticeSlides]);
    }, []);

    useEffect(() => { fetchSlides(); }, [fetchSlides]);

    // Auto-advance every 5 seconds
    useEffect(() => {
        if (slides.length <= 1) return;
        const timer = setInterval(() => {
            setCurrent(prev => (prev + 1) % slides.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [slides.length]);

    if (slides.length === 0) return null;

    const slide = slides[current];
    /* Resolved through fileUrl, which is the whole reason that helper exists.
       Notices store a FULL url (noticeBoard builds base + path on upload); events store
       the RAW relative path ("/uploads/event/x.png"). Unresolved, a notice slide happened
       to work and an event slide could not: the browser asked sales.nexwaveintl.com for
       the file, and nginx's SPA fallback answered with index.html at status 200 — HTML
       where a PNG was wanted, so no image, no console error and no broken-image icon.
       fileUrl passes an absolute url straight through and puts the API origin in front of
       a relative one, so both kinds work. (user 2026-10-19: "Cant see the image in
       dashboard and also can't swipe") */
    const imageUrl = fileUrl(slide.slideshowImageUrl || slide.attachmentUrl);
    const hasImage = !!imageUrl;
    const openSlide = slide.eventId ? () => navigate(`/events/${slide.eventId}`) : undefined;

    const go = (step) => setCurrent(prev => (prev + step + slides.length) % slides.length);

    /* Swipe. There were no touch handlers at all — on a phone the only way to change
       slide was the arrows, which are small and sat under a thumb. A horizontal drag now
       moves the slide; a vertical one is left alone so scrolling the dashboard past the
       banner does not shuffle it. */
    const onTouchStart = (e) => {
        const t = e.touches[0];
        touch.current = { x: t.clientX, y: t.clientY, moved: false };
    };
    const onTouchMove = (e) => {
        const t = e.touches[0];
        if (Math.abs(t.clientX - touch.current.x) > 10) touch.current.moved = true;
    };
    const onTouchEnd = (e) => {
        const t = e.changedTouches[0];
        const dx = t.clientX - touch.current.x;
        const dy = t.clientY - touch.current.y;
        if (slides.length > 1 && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
            go(dx < 0 ? 1 : -1);
            touch.current.moved = true;
        }
    };
    // A swipe must not also count as a tap on the banner, or every swipe would navigate
    // to the event instead of moving the slide.
    const handleClick = () => { if (!touch.current.moved && openSlide) openSlide(); };

    return (
        <Box
            onClick={handleClick}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            sx={{
            position: 'relative', borderRadius: '14px', overflow: 'hidden', mb: 3,
            cursor: openSlide ? 'pointer' : 'default',
            // Let the page scroll vertically while this still reads horizontal drags.
            touchAction: 'pan-y',
            background: hasImage ? 'none' : 'linear-gradient(135deg, #0C1B3A, #1A3A6B)',
            // Fill the column width; scale the height up on larger screens (was capped
            // at 280, which looked tiny once the dashboard went full-width). Mobile
            // stays compact. Suggested: ~200px phone, ~480px desktop.
            width: '100%', aspectRatio: '16/9', maxHeight: { xs: 200, sm: 320, md: 480 },
            display: 'flex', alignItems: 'center',
        }}>
            {/* Background image */}
            {hasImage && (
                <Box sx={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundImage: `url(${imageUrl})`,
                    backgroundSize: 'cover', backgroundPosition: 'center',
                    '&::after': {
                        content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)',
                    }
                }} />
            )}

            {/* Content */}
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 1, p: 2, pb: slides.length > 1 ? 4 : 2 }}>
                {slide.content && (
                    <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.85)', fontFamily: "'Poppins', sans-serif", maxWidth: 500, lineHeight: 1.4 }}>
                        {slide.content.length > 120 ? slide.content.substring(0, 120) + '...' : slide.content}
                    </Typography>
                )}
            </Box>

            {/* Navigation arrows */}
            {slides.length > 1 && (
                <>
                    {/* stopPropagation on every control: they sit INSIDE the banner, which
                        is itself clickable, so without it tapping an arrow or a dot also
                        fired openSlide and navigated to the event instead of changing
                        slide. (user 2026-10-19) */}
                    <IconButton onClick={(e) => { e.stopPropagation(); go(-1); }}
                        aria-label="Previous slide"
                        sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' } }}>
                        <ChevronLeftIcon />
                    </IconButton>
                    <IconButton onClick={(e) => { e.stopPropagation(); go(1); }}
                        aria-label="Next slide"
                        sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' } }}>
                        <ChevronRightIcon />
                    </IconButton>

                    {/* Dots */}
                    <Box sx={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 0.75 }}>
                        {slides.map((_, i) => (
                            <Box key={i} onClick={(e) => { e.stopPropagation(); setCurrent(i); }}
                                sx={{
                                    width: i === current ? 20 : 8, height: 8, borderRadius: 4, cursor: 'pointer',
                                    backgroundColor: i === current ? '#1A8CFF' : 'rgba(255,255,255,0.4)',
                                    transition: 'all 0.3s',
                                }} />
                        ))}
                    </Box>
                </>
            )}
        </Box>
    );
}
